/*
 * Catálogo de acciones directas de esta paleta de comandos (Ctrl+K).
 *
 * Cada acción:
 *  - id:           identificador único
 *  - label:        texto visible en la paleta
 *  - keywords:     términos extra que también matchean en la búsqueda
 *  - icon:         clase CSS del ícono (fa/cui)
 *  - moduleRouter: gate de acceso — debe coincidir con el campo `router` de
 *                  algún módulo en session.auth.user.permissions.menu.
 *                  null = visible para cualquier staff.
 *  - to:           destino para $router.push ({ name, query?, params? })
 *
 * Para agregar una acción nueva: si el destino es una vista existente alcanza
 * con sumar la entrada acá. Si la acción debe abrir un formulario/modal
 * directamente, la vista destino tiene que soportar un query param que lo
 * dispare (ver crudReminders.vue con ?new=1 como referencia).
 */

import Helper from '@/handler/helper'
import Param from '@/config/parameters'
import Session from '@/handler/session'
import Modules from '@/config/modules'
import Profiles from '@/config/profiles'

function hasActiveModuleView(moduleId, reference) {
  var session = Session.getSession()
  var modules = session.auth && session.auth.user.permissions.modules
  return Array.isArray(modules) && modules.some(module => module.id == moduleId && module.active) &&
    Helper.showAccessView({ module_id: moduleId, profile_id: Profiles.PERSONAL }, reference)
}

function getQuickActions() {
  return [
    {
      id: 'home',
      label: 'Inicio',
      keywords: ['dashboard', 'tablero', 'widgets', 'mi espacio', 'home', 'principal'],
      icon: 'dashboard',
      moduleRouter: null,
      to: { name: Helper.hasParametersAccess(Param.P81) ? 'MySpaceStaff' : 'DashboardStaff' },
    },
    // "Nuevo recordatorio" / "Nueva tarea" / "Nuevo presupuesto" ya no están
    // acá: search.js ahora indexa esas vistas directamente desde
    // permissions.items + router/viewsRegistry.js (ver collectPermissionViews
    // en search.js). Esto solo debería tener acciones que hagan algo que una
    // vista sola no hace — como estas, que abren el modal de alta
    // directamente vía ?new=1 (agregado a cada componente para esto).

    // VENTAS — mismo verbo "Crear nuevo/a ..." para todas, así se leen como
    // un solo conjunto en la paleta. Los verbos viejos (facturar, emitir,
    // generar...) quedan en keywords para que sigan matcheando.
    {
      id: 'budgets_new',
      label: 'Crear nuevo presupuesto',
      keywords: ['crear presupuesto', 'nuevo presupuesto', 'generar presupuesto', 'presupuestar'],
      icon: 'file-earmark-text',
      moduleRouter: 'budgets',
      to: { name: 'BudgetsStaffCrudBudget', query: { new: '1' } },
    },
    {
      id: 'orders_new',
      label: 'Crear nuevo pedido',
      keywords: ['nuevo pedido', 'crear pedido', 'generar pedido', 'cargar pedido'],
      icon: 'cart',
      moduleRouter: 'orders',
      to: { name: 'OrdersStaffCrudOrders', query: { new: '1' } },
    },
    {
      id: 'sales_new_factura',
      label: 'Crear nueva factura',
      keywords: ['nueva venta', 'crear venta', 'generar venta', 'factura', 'facturar'],
      icon: 'file-earmark-text',
      moduleRouter: 'sales',
      to: { name: 'SalesStaffSales', query: { new: '1', type: 'factura' } },
    },
    {
      id: 'sales_new_notacredito',
      label: 'Crear nueva nota de crédito',
      keywords: ['nota de credito', 'nc', 'generar nota de credito'],
      icon: 'file-earmark-text',
      moduleRouter: 'sales',
      to: { name: 'SalesStaffSales', query: { new: '1', type: 'notacredito' } },
    },
    {
      id: 'sales_new_notadebito',
      label: 'Crear nueva nota de débito',
      keywords: ['nota de debito', 'nd', 'generar nota de debito'],
      icon: 'file-earmark-text',
      moduleRouter: 'sales',
      to: { name: 'SalesStaffSales', query: { new: '1', type: 'notadebito' } },
    },
    {
      id: 'remits_new',
      label: 'Crear nuevo remito',
      keywords: ['nuevo remito', 'crear remito', 'generar remito', 'remito de venta'],
      icon: 'truck',
      moduleRouter: 'remits',
      to: { name: 'RemitsStaffRemits', query: { new: '1' } },
    },
    {
      id: 'receipts_new',
      label: 'Crear nuevo recibo',
      keywords: ['nuevo recibo', 'crear recibo', 'generar recibo', 'emitir recibo', 'cobranza'],
      icon: 'cash-stack',
      moduleRouter: 'receipts-erp',
      to: { name: 'ReceiptsErpStaffReceipts', query: { new: '1' } },
    },

    // COMPRAS — mismo criterio que ventas, "Crear nuevo/a ...".
    {
      id: 'purchase_orders_new',
      label: 'Crear nueva orden de compra',
      keywords: ['nueva orden de compra', 'crear orden de compra', 'generar orden de compra', 'oc'],
      icon: 'file-earmark-text',
      moduleRouter: 'purchase-orders',
      to: { name: 'PurchaseOrdersStaffPurchaseOrders', query: { new: '1' } },
    },
    {
      id: 'purchase_remits_new',
      label: 'Crear nuevo remito de compra',
      keywords: ['nuevo remito de compra', 'crear remito de compra', 'generar remito de compra', 'remito normal'],
      icon: 'truck',
      moduleRouter: 'purchase-remits',
      to: { name: 'PurchaseStaffPurchaseRemits', query: { new: '1' } },
    },
    {
      id: 'purchase_remits_new_from_order',
      label: 'Crear nuevo remito de compra desde orden de compra',
      keywords: ['remito desde orden de compra', 'remito desde oc', 'alta desde orden de compra', 'generar remito desde orden de compra'],
      icon: 'truck',
      moduleRouter: 'purchase-remits',
      to: { name: 'PurchaseStaffPurchaseRemits', query: { new: '1', mode: 'from_order' } },
    },
    {
      id: 'purchase_new',
      label: 'Crear nuevo comprobante de compra',
      keywords: ['nueva factura de compra', 'crear factura de compra', 'cargar factura', 'factura de compra'],
      icon: 'file-earmark-text',
      moduleRouter: 'purchase',
      to: { name: 'PurchaseStaffPurchase', query: { new: '1' } },
    },
    {
      id: 'payments_erp_new',
      label: 'Crear nuevo pago',
      keywords: ['nuevo pago', 'crear pago', 'generar pago', 'hacer un pago'],
      icon: 'cash-stack',
      moduleRouter: 'payments-erp',
      to: { name: 'PaymentsErpStaffPayments', query: { new: '1' } },
    },

    // TAREAS Y RECORDATORIOS
    {
      id: 'tasks_new_personal',
      label: 'Cargar tarea personal',
      keywords: ['nueva tarea', 'crear tarea', 'tarea personal'],
      icon: 'tasks',
      moduleRouter: 'tasks',
      to: { name: 'TasksStaffCrud', query: { new: '1', mode: 'personal' } },
    },
    {
      id: 'tasks_new_customer',
      label: 'Cargar tarea a un cliente',
      keywords: ['nueva tarea', 'crear tarea', 'tarea de cliente', 'tarea cliente'],
      icon: 'tasks',
      moduleRouter: 'tasks',
      available: () => hasActiveModuleView(Modules.CLIENTES, 'crud'),
      to: { name: 'TasksStaffCrud', query: { new: '1', mode: 'customer' } },
    },
    {
      id: 'tasks_new_project',
      label: 'Cargar tarea a un proyecto',
      keywords: ['nueva tarea', 'crear tarea', 'tarea de proyecto', 'tarea proyecto'],
      icon: 'tasks',
      moduleRouter: 'tasks',
      available: () => hasActiveModuleView(Modules.PROYECTOS, 'crudProject'),
      to: { name: 'TasksStaffCrud', query: { new: '1', mode: 'project' } },
    },
    {
      id: 'tasks_new_supplier',
      label: 'Cargar tarea a un proveedor',
      keywords: ['nueva tarea', 'crear tarea', 'tarea de proveedor', 'tarea proveedor'],
      icon: 'tasks',
      moduleRouter: 'tasks',
      available: () => hasActiveModuleView(Modules.PROVEEDORES, 'crud'),
      to: { name: 'TasksStaffCrud', query: { new: '1', mode: 'supplier' } },
    },
    {
      id: 'reminders_new',
      label: 'Agregar nuevo recordatorio',
      keywords: ['nuevo recordatorio', 'crear recordatorio', 'programar recordatorio'],
      icon: 'alarm',
      moduleRouter: 'reminders',
      to: { name: 'RemindersStaffCrud', query: { new: '1' } },
    },

    // PROYECTOS
    {
      id: 'projects_hours_new',
      label: 'Cargar horas a proyecto',
      keywords: ['nuevas horas', 'cargar horas', 'horas de proyecto', 'imputar horas'],
      icon: 'schedule',
      moduleRouter: 'projects',
      to: { name: 'ProjectStaffCrudHours', query: { new: '1', mode: 'project' } },
    },

    // CLIENTES Y PROVEEDORES
    {
      id: 'customers_new',
      label: 'Cargar nuevo cliente',
      keywords: ['nuevo cliente', 'crear cliente', 'alta cliente'],
      icon: 'business',
      moduleRouter: 'customers',
      to: { name: 'CustomerListStaff', query: { add: 'open' } },
    },
    {
      id: 'suppliers_new',
      label: 'Cargar nuevo proveedor',
      keywords: ['nuevo proveedor', 'crear proveedor', 'alta proveedor'],
      icon: 'business',
      moduleRouter: 'suppliers',
      to: { name: 'SuppliersListStaff', query: { add: 'open' } },
    },

    // DEPÓSITOS
    {
      id: 'stock_adjust_positive',
      label: 'Cargar ajuste positivo de stock',
      keywords: ['ajuste positivo', 'ingreso manual', 'sumar stock'],
      icon: 'arrow-up',
      moduleRouter: 'deposits',
      to: { name: 'DepositsStaffCrudDepositsOperations', query: { new: '1', mode: 'add-manual' } },
    },
    {
      id: 'stock_adjust_negative',
      label: 'Cargar ajuste negativo de stock',
      keywords: ['ajuste negativo', 'egreso manual', 'restar stock'],
      icon: 'arrow-down',
      moduleRouter: 'deposits',
      to: { name: 'DepositsStaffCrudDepositsOperations', query: { new: '1', mode: 'sub-manual' } },
    },
    {
      id: 'deposits_transfer_new',
      label: 'Cargar transferencia entre depósitos',
      keywords: ['transferencia entre depositos', 'mover stock', 'transferir stock', 'generar transferencia'],
      icon: 'arrow-left-right',
      moduleRouter: 'deposits',
      to: { name: 'DepositsStaffCrudDepositsOperations', query: { new: '1', mode: 'transferencia' } },
    },

    // FINANZAS — la acción abre el movimiento con el origen vacío para que
    // el usuario elija la caja/cuenta adecuada antes de guardar.
    {
      id: 'cash_income_new',
      label: 'Cargar ingreso de caja',
      keywords: ['nuevo ingreso', 'ingreso de caja', 'ajuste positivo', 'cobro manual'],
      icon: 'add',
      moduleRouter: 'finances',
      to: { name: 'FinancesStaffCashDetail', query: { new: '1', mode: 'cash-income' } },
    },
    {
      id: 'cash_expense_new',
      label: 'Cargar egreso de caja',
      keywords: ['nuevo egreso', 'egreso de caja', 'ajuste negativo', 'pago manual'],
      icon: 'remove',
      moduleRouter: 'finances',
      to: { name: 'FinancesStaffCashDetail', query: { new: '1', mode: 'cash-expense' } },
    },
    {
      id: 'cash_transfer_new',
      label: 'Cargar transferencia entre cajas',
      keywords: ['transferencia de caja', 'transferir entre cajas', 'mover dinero entre cuentas'],
      icon: 'swap_horiz',
      moduleRouter: 'finances',
      to: { name: 'FinancesStaffCashDetail', query: { new: '1', mode: 'cash-transfer' } },
    },
  ]
}

export default getQuickActions
